import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { withLocalize, LocalizeContextProps } from 'react-localize-redux';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { ReduxStore } from '../../../constants/types';
import { Billable } from './types';
import { pushBillables } from '../../../redux/actions/billingActions';
import { filterBillablesByCategory, getBillableCategories, getElementBillables } from './billItemAddHelpers';

// SHER-460 — manual-billing prestation picker. Replaces the old full-width category
// chip-wall that read like page content. Flow: filter first (category chips), then
// search inside it via the "Concept / Prestation" field; clicking a result adds its
// row immediately via pushBillables([{ id }]) — the same path combos use — and the
// panel stays open for the next pick (one-by-one, no batch). Only ELEMENT billables
// are listed here; forfaits/physio/combos are added through their own selectors.

const T = 'hospital.billing.bill.selectors.';

interface Props {
    disabled?: boolean;
}

// Categorical colour palette for the chips — one stable colour per category, keyed by
// its index in the sorted category list. Mirrors the existing `categoryPillColor`
// approach in BillItems; categories are domain data with no theme token, so a fixed
// palette (not theme.palette) is the right tool here.
const CATEGORY_PALETTE = [
    '#BA7517', '#378ADD', '#A32D2D', '#639922', '#534AB7', '#1D9E75',
    '#D85A30', '#D4537E', '#0F6E56', '#185FA5', '#854F0B', '#27500A',
    '#993C1D', '#3C3489', '#72243E', '#0C447C', '#5F5E5A', '#444441', '#085041',
];

const formatAmount = (amount: number) => `${Number(amount ?? 0).toLocaleString('fr-FR')}`;

const PrestationSelectorInner: React.FC<Props & LocalizeContextProps> = ({ disabled, translate }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const billables = useSelector((state: ReduxStore) => state.billing.data.billablesCurrentBill ?? []);
    const loadingBillables = useSelector((state: ReduxStore) => state.billing.loading);
    const [category, setCategory] = useState('');
    const [query, setQuery] = useState('');

    const elementBillables = useMemo(() => getElementBillables(billables as Billable[]), [billables]);
    const categories = useMemo(() => getBillableCategories(elementBillables), [elementBillables]);
    const colorMap = useMemo(() => {
        const map = new Map<string, string>();
        categories.forEach((c, i) => map.set(c, CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]));
        return map;
    }, [categories]);

    const results = useMemo(() => {
        const byCategory = filterBillablesByCategory(elementBillables, category || null);
        const q = query.trim().toLowerCase();
        return q ? byCategory.filter((b) => (b.concept ?? '').toLowerCase().includes(q)) : byCategory;
    }, [elementBillables, category, query]);

    const colorFor = (c: string) => colorMap.get(c) ?? theme.palette.grey[600];

    const onAdd = (b: Billable) => {
        if (disabled) return;
        dispatch(pushBillables([{ id: b.id }]));
    };

    // Match BillItemAddActionBar: don't render against a stale/empty list while the
    // billables reload (e.g. after an insurance change).
    if (loadingBillables) return null;

    const t = (key: string) => translate(`${T}${key}`).toString();
    const allLabel = t('all_categories');

    const chip = (label: string, value: string) => {
        const active = category === value;
        const c = value ? colorFor(value) : theme.palette.grey[600];
        return (
            <Box
                key={label}
                component="button"
                type="button"
                disabled={disabled}
                onClick={() => setCategory(value)}
                sx={{
                    flex: '0 0 auto', border: 'none', cursor: disabled ? 'default' : 'pointer', fontSize: 12,
                    px: 1.25, py: 0.4, borderRadius: 14, fontWeight: active ? 500 : 400, whiteSpace: 'nowrap',
                    backgroundColor: active ? c : `${c}22`, color: active ? '#fff' : c,
                }}
            >
                {label}
            </Box>
        );
    };

    return (
        <Box data-testid="prestation-selector" sx={{ width: '100%', mb: 2 }}>
            <TextField
                size="small"
                fullWidth
                label={t('select_prestation')}
                placeholder={t('search_placeholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={disabled}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                            {query ? (
                                <IconButton size="small" edge="start" aria-label={t('clear')} data-testid="prestation-clear" onClick={() => setQuery('')} sx={{ ml: 0.25 }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </InputAdornment>
                    ),
                }}
            />
            <Paper elevation={0} sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid #d4d4d4', boxShadow: '0 6px 18px -8px rgba(0,0,0,0.25)' }}>
                {/* tinted header with an accent bar so the panel reads as its own component */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, backgroundColor: '#f4f8fd', borderBottom: '1px solid #e3edf7' }}>
                    <Box sx={{ width: 4, height: 18, borderRadius: 1, backgroundColor: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" fontWeight={500} sx={{ flex: 1 }}>{t('select_prestation_title')}</Typography>
                    <Box component="span" sx={{ fontSize: 11, px: 1.1, py: 0.25, borderRadius: 11, color: category ? colorFor(category) : 'text.secondary', backgroundColor: category ? `${colorFor(category)}22` : '#eee' }}>
                        {category || allLabel} · {results.length}
                    </Box>
                </Box>
                {/* category chips — wrap into a few rows, capped with internal scroll */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1.75, py: 1.25, maxHeight: 96, overflowY: 'auto', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                    {chip(allLabel, '')}
                    {categories.map((c) => chip(c, c))}
                </Box>
                {/* results in a responsive grid → fills the width, collapses to 1 column on small laptops */}
                <Box data-testid="prestation-results" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1px', backgroundColor: '#eee', maxHeight: 240, overflowY: 'auto' }}>
                    {results.map((b) => (
                        <Box key={b.id} onClick={() => onAdd(b)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, backgroundColor: '#fff', cursor: disabled ? 'default' : 'pointer', '&:hover': disabled ? {} : { backgroundColor: '#f7f7f7' } }}>
                            <AddIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                            <Typography variant="body2" sx={{ flex: 1 }}>{b.concept}</Typography>
                            <Typography variant="caption" color="textSecondary">{formatAmount(b.amount)}</Typography>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ px: 1.75, py: 0.75, borderTop: '1px solid #eee', backgroundColor: '#fafafa' }}>
                    <Typography variant="caption" color="textSecondary">{t('add_hint')}</Typography>
                </Box>
            </Paper>
        </Box>
    );
};

export const PrestationSelector = withLocalize(PrestationSelectorInner);

export default PrestationSelector;
